import { CORAL_600, GRAY_200, GRAY_300, GRAY_900, WHITE } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { NameAvatar } from 'components/common/Avatars/NameAvatar.component'
import { useTranslation } from 'react-i18next'
import { useWorkspaceInvitationActions } from 'utils/hooks'
import { WorkspaceUserProfile } from 'models/user'

export enum PendingWorkspaceInvitationItemVariant {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

interface Props {
  workspace: WorkspaceUserProfile
  variant: PendingWorkspaceInvitationItemVariant
}

export const PendingWorkspaceInvitationItem: React.FC<Props> = ({ workspace, variant }) => {
  const { t } = useTranslation(['workspace_profile', 'actions'])
  const { handleDecline, handleAccept, isDeclinePending, isAcceptPending } = useWorkspaceInvitationActions()

  const isDesktop = variant === PendingWorkspaceInvitationItemVariant.DESKTOP

  return (
    <Stack
      gap={isDesktop ? '.8rem' : '1.2rem'}
      padding=".8rem"
      width="100%"
      borderRadius=".4rem"
      sx={{
        backgroundColor: GRAY_200,
        flexShrink: 0,
      }}
    >
      <Stack
        flexDirection="row"
        alignItems="center"
        overflow="hidden"
        {...(isDesktop
          ? {
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: `1px solid ${GRAY_300}`,
            paddingBottom: '.8rem',
          }
          : {
            gap: '.8rem',
          }
        )}
      >
        {!isDesktop &&
          <Stack flexShrink={0}>
            <NameAvatar
              name={workspace.name}
              size="2.4rem"
              fontWeight={350}
              textVariant='text-xs'
              backgroundColor={CORAL_600}
              borderColor="transparent"
              textColor={WHITE}
              elevation="none"
            />
          </Stack>
        }

        <Typography
          variant="text-md"
          color={GRAY_900}
          fontWeight={isDesktop ? 350 : 550}
          sx={{
            ...(isDesktop && { height: '2.4rem' }),
            ...(!isDesktop && { lineHeight: '3.2rem' }),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {workspace.name}
        </Typography>

        <MUIBadge color="blue" size="sm" label={t('pending_invitation')} sx={{ flexShrink: 0 }} />
      </Stack>

      <Stack flexDirection="row" gap=".8rem">
        <MUIButton
          type="secondaryBorder"
          variant="outlined"
          size="xs"
          borderStyle="rounded"
          isLoading={isDeclinePending(workspace.invitationId)}
          onClick={() => handleDecline({ workspaceId: workspace.id, invitationId: workspace.invitationId })}
          sx={{ flex: 1 }}
        >
          {t('actions:Decline')}
        </MUIButton>

        <MUIButton
          size="xs"
          borderStyle="rounded"
          isLoading={isAcceptPending(workspace.invitationId)}
          onClick={() => handleAccept({ workspaceId: workspace.id, invitationId: workspace.invitationId })}
          sx={{ flex: 1 }}
        >
          {t('actions:Accept')}
        </MUIButton>
      </Stack>
    </Stack>
  )
}
